
import * as React from "react"
import { toast as sonnerToast, type ToastT } from "sonner"

export type ToastProps = React.ComponentPropsWithoutRef<typeof sonnerToast>

export type ToasterToast = {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
  variant?: "default" | "destructive" | "success" | "warning" | "info"
  onOpenChange?: (open: boolean) => void
}

const actionTypes = {
  DEFAULT: "default",
  DESTRUCTIVE: "destructive",
  SUCCESS: "success",
  WARNING: "warning",
  INFO: "info"
}

export const useToast = () => {
  const [toasts, setToasts] = React.useState<ToasterToast[]>([])

  const dismiss = React.useCallback((toastId?: string) => {
    setToasts((toasts) => toasts.filter((toast) => toast.id !== toastId))
  }, [])

  const toast = React.useMemo(
    () => ({
      dismiss,
      toast: (props: Omit<ToasterToast, "id"> & { id?: string }) => {
        const id = props.id || String(Date.now())
        setToasts((toasts) => {
          // If toast with the same id exists, remove it first
          const existingToasts = toasts.filter((toast) => toast.id !== id)
          return [...existingToasts, { ...props, id }]
        })
        return id
      },
      error: (title: string, description?: string) => {
        return sonnerToast.error(title, { description });
      },
      success: (title: string, description?: string) => {
        return sonnerToast.success(title, { description });
      },
      warning: (title: string, description?: string) => {
        return sonnerToast(title, { 
          description,
          style: { backgroundColor: 'var(--warning)', color: 'var(--warning-foreground)' } 
        });
      },
      info: (title: string, description?: string) => {
        return sonnerToast.info(title, { description });
      },
    }),
    [dismiss]
  )

  return {
    ...toast,
    toasts,
  }
}

// Export a singleton toast instance for direct imports
export const toast = {
  // Direct methods that use sonner toast
  error: (title: string, description?: string) => {
    return sonnerToast.error(title, { description });
  },
  success: (title: string, description?: string) => {
    return sonnerToast.success(title, { description });
  },
  warning: (title: string, description?: string) => {
    return sonnerToast(title, { 
      description,
      style: { backgroundColor: 'var(--warning)', color: 'var(--warning-foreground)' } 
    });
  },
  info: (title: string, description?: string) => {
    return sonnerToast.info(title, { description });
  },
  
  // Compatibility method for useToast().toast()
  toast: (props: Omit<ToasterToast, "id"> & { id?: string }) => {
    const id = props.id || String(Date.now());
    
    // If props is a string, just show it as a simple toast
    if (typeof props === 'string') {
      return sonnerToast(props);
    }
    
    // Handle object props properly
    if (typeof props === 'object' && props !== null) {
      if ('title' in props && 'description' in props) {
        const title = props.title ? String(props.title) : undefined;
        const description = props.description ? String(props.description) : undefined;
        return sonnerToast(title || '', { 
          description
        });
      } else if ('title' in props) {
        const title = props.title ? String(props.title) : '';
        return sonnerToast(title);
      }
    }
    
    // Fallback to just displaying a generic toast
    return sonnerToast('Notification');
  }
}
